import { Link } from "@tanstack/react-router";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { SERVICES } from "@/components/site/constants";
import { ResponsivePicture, type ResponsiveImage } from "@/components/site/images";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { GoogleRatingLine } from "@/components/site/GoogleRating";

export function LocalProofCard({ image, alt, town, title, to, children }: {
  image: ResponsiveImage; alt: string; town: string; title: string; to: string; children?: React.ReactNode;
}) {
  return (
    <div className="my-6 not-prose rounded-xl border border-border bg-card overflow-hidden grid sm:grid-cols-[200px_1fr]">
      <ResponsivePicture image={image} alt={alt} sizes="(min-width: 640px) 200px, 100vw" imgClassName="w-full h-full object-cover" />
      <div className="p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Local proof — {town}, FL</p>
        <p className="mt-1 font-semibold">{title}</p>
        {children && <p className="mt-1 text-sm text-muted-foreground">{children}</p>}
        <Link to={to} className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">View the project</Link>
      </div>
    </div>
  );
}

export function CityPageBody({
  city,
  slug,
  intro,
  localNotes,
  services = SERVICES.map(s => s.title),
  image,
  imageAlt,
  imageCaption,
  faqSection,
  recentProjectSection,
  localProof,
  permitNote,
}: {
  city: string;
  slug?: string;
  intro: React.ReactNode;
  localNotes: React.ReactNode;
  services?: readonly string[];
  image?: ResponsiveImage;
  imageAlt?: string;
  imageCaption?: string;
  faqSection?: React.ReactNode;
  recentProjectSection?: React.ReactNode;
  localProof?: React.ReactNode;
  permitNote?: React.ReactNode;
}) {
  return (
    <>
      {slug && (
        <Breadcrumbs items={[{ label: "Service Areas", href: "/service-areas" }, { label: `${city}, FL`, href: slug }]} />
      )}
      <PageHero
        eyebrow="Local Roofing"
        title={`Roofing Company in ${city}, FL`}
        subtitle={`Family-owned, licensed and insured roofing services for ${city} homeowners. Free Roof Inspection & Estimate — honest answers from a local roofer.`}
      />
      <ServicePageBody title={`Roofing in ${city}`} service={`Roofing in ${city}, FL`}>
        {localProof && (
          <>
            <div className="not-prose mb-2"><GoogleRatingLine /></div>
            {localProof}
          </>
        )}
        <h2>Roofing Services for {city} Homeowners</h2>
        {intro}

        {image && imageAlt && (
          <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
            <ResponsivePicture
              image={image}
              alt={imageAlt}
              sizes="(min-width: 1024px) 66vw, 100vw"
              imgClassName="w-full h-auto"
            />
            {imageCaption && <figcaption className="p-3 text-sm text-muted-foreground">{imageCaption}</figcaption>}
          </figure>
        )}

        <h2>Common Roofing Problems We See in {city}</h2>
        {localNotes}

        <h2>Roof Repair and Replacement Options</h2>
        <p>
          Depending on what we find on your {city} roof, options may include a targeted <Link to="/roof-repair">roof repair</Link>, a full <Link to="/roof-replacement">roof replacement</Link>, or specific systems like <Link to="/metal-roofing">metal roofing</Link>, <Link to="/shingle-roofing">shingle roofing</Link> or <Link to="/flat-roofing">flat roofing</Link>. We'll walk you through the honest trade-offs.
        </p>

        {permitNote && (
          <>
            <h2>Roof Permits in {city}</h2>
            {permitNote}
          </>
        )}

        {localProof && (
          <>
            <h2>Helpful Guides for {city} Homeowners</h2>
            <ul>
              <li><Link to="/florida-roof-insurance-rules">Florida roof insurance rules</Link> — what Florida law and insurers look at when it comes to your roof.</li>
              <li><Link to="/roof-cost-citrus-county">Roof cost in Citrus County</Link> — our 2026 price ranges for metal, shingle and mobile home roofs.</li>
              <li><Link to="/mobile-home-roofing">Mobile home roofing</Link> — metal and TPO roof-overs, coatings and leak repairs for manufactured homes.</li>
              <li><Link to="/citrus-county-roof-permits">Citrus County roof permits</Link> — how re-roof permits and the Notice of Commencement work.</li>
            </ul>
          </>
        )}

        {faqSection}

        {recentProjectSection}

        <h2>Why Choose Destination Roofing</h2>
        <ul>
          {services.map(s => {
            const service = SERVICES.find(sv => sv.title === s);
            return (
              <li key={s}>
                {service ? <Link to={service.slug}>{s}</Link> : s}
              </li>
            );
          })}
        </ul>

        <h2>Free Roof Inspection in {city}</h2>
        <p>
          If you live in {city} and you're not sure whether your roof needs a repair or a full replacement, that's exactly what our <Link to="/roof-inspection">free roof inspection</Link> is for. We'll walk the roof, take pictures, explain what we find in plain language, and give you a written estimate.
        </p>
        <p>
          Read practical roofing advice on our <Link to="/blog">blog</Link> — including how to spot the signs your Citrus County roof may need repair after a storm.
        </p>
      </ServicePageBody>
      {slug && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              breadcrumbListJsonLd([{ label: "Service Areas", href: "/service-areas" }, { label: `${city}, FL`, href: slug }]),
              {
                "@context": "https://schema.org",
                "@type": "Service",
                name: `Roofing services in ${city}, FL`,
                serviceType: "Roofing Contractor",
                url: `https://destinationroofinginc.com${slug}`,
                provider: { "@id": ROOFING_CONTRACTOR_ID },
                areaServed: { "@type": "City", name: city, containedInPlace: "Citrus County, Florida" },
              },
            ]),
          }}
        />
      )}
    </>
  );
}
